import { clamp } from "../utils/formatters.js";

export const BULLISH_GOLD_KEYWORDS = [
  "weak dollar",
  "dollar falls",
  "rate cut",
  "fed dovish",
  "inflation fears",
  "geopolitical risk",
  "war",
  "recession",
  "banking crisis",
  "safe haven",
  "central bank gold buying",
  "central banks gold buying",
  "lower real yields",
  "treasury yields fall",
  "usd weakness",
  "yield falls",
  "yields fall",
];

export const BEARISH_GOLD_KEYWORDS = [
  "strong dollar",
  "dollar rises",
  "higher yields",
  "fed hawkish",
  "rate hike",
  "risk-on",
  "stocks rally",
  "inflation cools",
  "strong economy",
  "real yields rise",
  "yield rises",
  "yields rise",
];

const BULLISH_DRIVER_LABELS = {
  "weak dollar": "слабость доллара США",
  "dollar falls": "слабость доллара США",
  "rate cut": "ожидания снижения ставок",
  "fed dovish": "мягкие ожидания по ФРС",
  "inflation fears": "инфляционные опасения",
  "geopolitical risk": "геополитические риски",
  war: "геополитические риски",
  recession: "рецессионные риски",
  "banking crisis": "стресс в банковском секторе",
  "safe haven": "спрос на защитные активы",
  "central bank gold buying": "покупки золота центральными банками",
  "central banks gold buying": "покупки золота центральными банками",
  "lower real yields": "снижение реальных доходностей",
  "treasury yields fall": "снижение доходностей казначейских облигаций США",
  "usd weakness": "слабость доллара США",
};

const BEARISH_DRIVER_LABELS = {
  "strong dollar": "укрепление доллара США",
  "dollar rises": "укрепление доллара США",
  "higher yields": "рост доходностей",
  "fed hawkish": "жесткие ожидания по ФРС",
  "rate hike": "ожидания повышения ставок",
  "risk-on": "risk-on режим рынка",
  "stocks rally": "рост фондового рынка",
  "inflation cools": "замедление инфляции",
  "strong economy": "сильная экономика",
  "real yields rise": "рост реальных доходностей",
};

function normalizeText(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function keywordHits(text, keywords) {
  return keywords.filter((keyword) => text.includes(keyword));
}

export function sentimentFromScore(score) {
  if (score >= 58) return "bullish";
  if (score <= 42) return "bearish";
  return "neutral";
}

export function calculateNewsKeywordScore(news = [], macro = {}) {
  const articles = Array.isArray(news) ? news.slice(0, 20) : [];
  let bullishHits = [];
  let bearishHits = [];
  const analyzedArticles = articles.map((article) => {
    const text = normalizeText(`${article?.title ?? ""} ${article?.summary ?? ""} ${article?.rawText ?? ""}`);
    const bullish = keywordHits(text, BULLISH_GOLD_KEYWORDS);
    const bearish = keywordHits(text, BEARISH_GOLD_KEYWORDS);
    bullishHits = bullishHits.concat(bullish);
    bearishHits = bearishHits.concat(bearish);
    const articleScore = clamp(50 + bullish.length * 10 - bearish.length * 10);
    return {
      title: String(article?.title ?? "Новость без заголовка").slice(0, 180),
      sentiment: sentimentFromScore(articleScore),
      impact: Math.round(articleScore),
      horizon: "short-term",
      reason:
        bullish.length || bearish.length
          ? `Keyword-анализ нашел: ${[...bullish, ...bearish].slice(0, 3).join(", ")}.`
          : "Явных ключевых слов по золоту не найдено.",
    };
  });

  let score = 50 + bullishHits.length * 5 - bearishHits.length * 5;
  if (Number(macro?.usdIndex) > 105) score -= 3;
  if (Number(macro?.realYield) > 2) score -= 5;
  if (Number(macro?.inflation) > 3) score += 3;
  score = Math.round(clamp(score));

  const bullishDrivers = bullishHits.map((keyword) => BULLISH_DRIVER_LABELS[keyword] ?? keyword);
  const bearishDrivers = bearishHits.map((keyword) => BEARISH_DRIVER_LABELS[keyword] ?? keyword);
  const sentiment = sentimentFromScore(score);
  const keyDrivers = unique(sentiment === "bearish" ? bearishDrivers : bullishDrivers).slice(0, 6);
  const riskWarnings = [];

  if (bearishHits.length > 0 && bullishHits.length > 0) {
    riskWarnings.push("Новостной фон смешанный, возможны быстрые смены направления.");
  }
  if (articles.length === 0) {
    riskWarnings.push("Актуальные новости недоступны, keyword fallback использует только макро-данные.");
  }
  if (Number(macro?.realYield) > 2) {
    riskWarnings.push("Высокие реальные доходности могут давить на золото.");
  }

  return {
    overallSentiment: sentiment,
    score,
    summary:
      sentiment === "bullish"
        ? "Keyword-анализ видит осторожно конструктивные импульсы для золота, прежде всего из-за рисков и ожиданий по ставкам."
        : sentiment === "bearish"
          ? "Keyword-анализ видит скорее сдерживающие факторы для золота, прежде всего доллар США и доходности."
          : "Keyword-анализ пока не видит четкого направления для золота.",
    keyDrivers: keyDrivers.length ? keyDrivers : ["смешанный новостной фон"],
    riskWarnings,
    shortTermView: "Краткосрочно важна реакция на доллар США, доходности и макро-события.",
    mediumTermView: "Среднесрочно ключевыми остаются реальные доходности, инфляционные ожидания и коммуникация ФРС.",
    longTermView: "Долгосрочно важны спрос центральных банков, реальные доходности и геополитические риски.",
    articles: analyzedArticles,
    source: "Keyword fallback",
    isFallback: true,
  };
}

export function safeParseGeminiJson(rawText) {
  if (!rawText || typeof rawText !== "string") return null;
  const trimmed = rawText.trim();
  const withoutFence = trimmed
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```$/i, "")
    .trim();

  const firstBrace = withoutFence.indexOf("{");
  const lastBrace = withoutFence.lastIndexOf("}");
  const jsonCandidate =
    firstBrace >= 0 && lastBrace > firstBrace ? withoutFence.slice(firstBrace, lastBrace + 1) : withoutFence;

  try {
    const parsed = JSON.parse(jsonCandidate);
    return normalizeGeminiAnalysis(parsed);
  } catch {
    return null;
  }
}

export function normalizeGeminiAnalysis(value) {
  if (!value || typeof value !== "object") return null;
  const sentiment = ["bullish", "bearish", "neutral"].includes(value.overallSentiment)
    ? value.overallSentiment
    : sentimentFromScore(Number(value.score));

  return {
    overallSentiment: sentiment,
    score: Math.round(clamp(Number(value.score ?? 50))),
    summary: String(value.summary ?? "Gemini не вернул сводку.").slice(0, 900),
    keyDrivers: Array.isArray(value.keyDrivers) ? value.keyDrivers.map(String).slice(0, 8) : [],
    riskWarnings: Array.isArray(value.riskWarnings) ? value.riskWarnings.map(String).slice(0, 8) : [],
    shortTermView: String(value.shortTermView ?? "").slice(0, 600),
    mediumTermView: String(value.mediumTermView ?? "").slice(0, 600),
    longTermView: String(value.longTermView ?? "").slice(0, 600),
    articles: Array.isArray(value.articles)
      ? value.articles.slice(0, 20).map((article) => ({
          title: String(article?.title ?? "Новость").slice(0, 180),
          sentiment: ["bullish", "bearish", "neutral"].includes(article?.sentiment) ? article.sentiment : "neutral",
          impact: Math.round(clamp(Number(article?.impact ?? 50))),
          horizon: ["intraday", "short-term", "medium-term", "long-term"].includes(article?.horizon)
            ? article.horizon
            : "short-term",
          reason: String(article?.reason ?? "").slice(0, 500),
        }))
      : [],
    source: "Gemini",
    isFallback: false,
  };
}
